import * as React from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { observable, runInAction, toJS } from 'mobx';
import { HolderOutlined, SettingOutlined } from '@ant-design/icons';
import {
  // Button,
  Row,
  Col,
  Checkbox,
  Popover,
  Form,
  Button,
  Space,
  Divider,
  Typography,
} from 'antd';
import AppController from '@/api/seller/Controller/AppController';
import type { DataTableApiInstance } from '../index';
import { useIntl } from 'react-intl';
import { find, findIndex, has } from 'lodash-es';
import {SortableContainer, SortableElement} from 'react-sortable-hoc';
import styles from '../index.module.less';
import { arrayMove } from '@/common/functions';

type ColumnSettingsPopoverProps = {
  children?: React.ReactNode;
  table: DataTableApiInstance;
};

const SortableItem = SortableElement<any>(({ item }: any) =>
  <div className={styles['sort-item']}><HolderOutlined /> {item?.title}</div>
);

const SortableList = SortableContainer<any>(({items}: any) => {
  return (
    <div className={styles['sort-list']}>
      {items.map((item:any, i:number) => (
        <SortableItem key={item?.key} index={i} item={item} />
      ))}
    </div>
  );
});

export const ColumnSettingsPopover = observer((props: ColumnSettingsPopoverProps) => {
  const intl = useIntl();
  const {
    children = <SettingOutlined/>,
    table,
  } = props;

  const [form] = Form.useForm();

  const store = useLocalObservable(() => ({
    open: false,
    loading: false,
    async querySettings() {
      if (!table.tableId) return;
      const { data: settings } = await AppController.getTableSettings({ id: table.tableId });
      if (settings) {
        table.setColumnsSettings(settings);
      }
    },
    setOpen(open: boolean) {
      if (open) {
        const columnsSettings = table.getColumnsSettings();

        this.columnsSettings = columnsSettings;
        this.columnsSortSettings = toJS(columnsSettings)
          .filter(d => !d.isExpandChild)
          .sort((a: any, b: any) => a?.sortIndex - b?.sortIndex);
        form.setFields([{
          name: 'columnsVisable',
          value: this.columnsSettings
            .filter((column: any) => column.visible === true)
            .map((column: any) => column.key)
        }, {
          name: 'columnsExpand',
          value: this.columnsSettings
            .filter((column: any) => column.expand === true)
            .map((column: any) => column.key)
        }]);
      }
      this.open = open;
    },
    resetSettings() {
      const columnsSettings = table.getDefaultColumnsSettings();
      this.columnsSettings = columnsSettings;
      this.columnsSortSettings = toJS(columnsSettings);
      form.setFields([{
        name: 'columnsVisable',
        value: this.columnsSettings
          .filter((column: any) => column.visible === true)
          .map((column: any) => column.key)
      }, {
        name: 'columnsExpand',
        value: this.columnsSettings
          .filter((column: any) => column.expand === true)
          .map((column: any) => column.key)
      }]);
    },
    async saveSettings(values: any) {
      const columnsVisable = values.columnsVisable || [];
      const columnsExpand = values.columnsExpand || [];
      this.loading = true;
      await AppController.addHideList({
        listId: table.tableId,
        columns: JSON.stringify(toJS(this.columnsSettings).map((d: any, i:number) => {
          const sortIndex = findIndex(this.columnsSortSettings, { key: d.key });
          let dItem: any = {
            key: d.key,
            fixed: d.fixed,
            visible: columnsVisable.indexOf(d.key) != -1,
            sortIndex: sortIndex == -1 ? i : sortIndex,
          };
          if (has(d,'expand')) {
            dItem.expand = columnsExpand.indexOf(d.key) != -1;
          }
          return dItem;
        }))
      }).finally(() => runInAction(() => this.loading = false));
      runInAction(() => {
        this.open = false;
      });
      await this.querySettings();
    },
    columnsSettings: [] as any[],
    columnsSortSettings: [] as any[],
    onSortEnd({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) {
      const newConfig = arrayMove(toJS(this.columnsSortSettings), oldIndex, newIndex);
      this.columnsSortSettings = newConfig;
    },
  }), { columnsSettings: observable.shallow, columnsSortSettings: observable.shallow });

  React.useEffect(() => {
    if (table.tableId) {
      store.querySettings();
    }
  }, [table.tableId]);

  return (
    <Popover
      title={<Typography.Text>{intl.formatMessage({defaultMessage:'列展示'})}</Typography.Text>}
      placement='bottomRight'
      trigger='click'
      open={store.open}
      onOpenChange={store.setOpen}
      overlayClassName={styles['column-settings-popover']}
      content={(
        <Form className='w-[560px]'
          form={form}
          onFinish={store.saveSettings}
        >
          <Row>
            <Col span={16} className='px-md pt-xs pb-xs'>
              <Form.Item noStyle name="columnsVisable">
                <Checkbox.Group>
                  <Row>
                    {store.columnsSettings.map((column: any) => (
                      <Col key={column.key} span={12} hidden={column.fixed || !column.title}>
                        <Checkbox value={column.key}>
                          {column.title}
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
              {find(store.columnsSettings, (column) => has(column, 'expand'))
                ? <>
                  <Typography.Text className='inline-block py-xss mt-xs'>
                    {intl.formatMessage({ defaultMessage: '字段展开' })}
                  </Typography.Text>
                  <Divider style={{ margin: '0 0 12px 0' }} />
                  <Form.Item noStyle name="columnsExpand">
                    <Checkbox.Group className='block'>
                      <Row>
                        {store.columnsSettings.map((column: any) => (
                          has(column,'expand')
                            ? <Col key={column.key} span={12} hidden={column.fixed || !column.title}>
                              <Checkbox value={column.key}>
                                {column.title}
                              </Checkbox>
                            </Col>
                            : null
                        ))}
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </>
                : null }
            </Col>
            <Col span={8} style={{ minHeight: 200 }}>
              <SortableList
                helperClass={styles['sort-helper']}
                items={store.columnsSortSettings}
                onSortEnd={store.onSortEnd}
              />
            </Col>
          </Row>
          <Divider className='mb-sm mt-0' />
          <Space className='flex justify-end pb-sm px-md'>
            <Button size='small'
              onClick={store.resetSettings}
            >
              {intl.formatMessage({ defaultMessage: '恢复默认' })}
            </Button>
            <Button htmlType='submit' type='primary' size='small' loading={store.loading}>
              {intl.formatMessage({ defaultMessage: '应用' })}
            </Button>
          </Space>
        </Form>
      )}
    >
      {children}
    </Popover>
  );
});

